import { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Rss, RefreshCw, DownloadCloud } from 'lucide-react';
import toast from 'react-hot-toast';
import { adminApi } from '@/features/admin/services/adminApi';
import ApiClient, { getErrorMessage } from '@/shared/services/api';
import { AdminRssFeed, ProductType, ProductTypeInfo } from '@/shared/types/api';
import { Spinner } from '@/shared/components/Spinner';
import { EmptyState } from '@/shared/components/EmptyState';
import { cn } from '@/shared/utils/cn';
import { traceIdFromError } from '@/shared/lib/traceId';
import { runStartedAt } from '@/features/admin/lib/logsLink';
import { FeedErrorToast, RequestFailedToast } from './FeedErrorToast';

interface FeedFormState {
  name: string;
  url: string;
  description: string;
  productType: ProductType;
  isActive: boolean;
}

/** "1 feed" / "6 feeds" — the toasts below count four different things. */
const plural = (count: number, one: string, many = `${one}s`) => `${count} ${count === 1 ? one : many}`;


/**
 * A whole manual run failed (a 500, or a timeout with no response): still link to the logs, by
 * the failing request's trace id when the response carried one, else to warnings since the start.
 */
const toastRunFailure = (error: unknown, fallback: string, startedAt: string) =>
  toast.error(
    <RequestFailedToast message={getErrorMessage(error, fallback)} traceId={traceIdFromError(error)} startedAt={startedAt} />,
    { duration: 10000 }
  );

const emptyForm: FeedFormState = {
  name: '',
  url: '',
  description: '',
  productType: ProductType.General,
  isActive: true,
};

export function AdminFeeds() {
  const [feeds, setFeeds] = useState<AdminRssFeed[]>([]);
  const [productTypes, setProductTypes] = useState<ProductTypeInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [editing, setEditing] = useState<AdminRssFeed | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState<FeedFormState>(emptyForm);

  const loadFeeds = async () => {
    try {
      setLoading(true);
      const [feedList, types] = await Promise.all([adminApi.getFeeds(), ApiClient.getProductTypes()]);
      setFeeds(feedList);
      setProductTypes(types);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load feeds'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFeeds();
  }, []);

  // The ingestion pass Hangfire otherwise runs at 06:00 UTC. This is the only path that records
  // item versions and archives notes Fenergo removed (SC-278) - the backfill below does neither,
  // which is what made the first SC-278 test round read as a failure.
  const onRefreshFeeds = async () => {
    setRefreshing(true);
    const startedAt = runStartedAt();
    try {
      const result = await adminApi.processFeeds();
      const feeds = plural(result.feedsProcessed, 'feed');

      const changes = [
        result.newItemsAdded > 0 ? plural(result.newItemsAdded, 'new item') : null,
        result.itemsUpdated > 0 ? plural(result.itemsUpdated, 'new version') : null,
        result.itemsArchived > 0 ? plural(result.itemsArchived, 'archived item') : null,
      ].filter(Boolean);

      if (changes.length > 0) {
        toast.success(`Read ${feeds}: ${changes.join(', ')}.`);
      } else {
        // The usual outcome when Fenergo has published nothing new - say it plainly, so a quiet
        // run is not mistaken for a broken button.
        toast.success(`Read ${feeds} — nothing changed at the source.`);
      }

      if (result.errors.length > 0) {
        // Links to this run's own lines in Admin → Logs; long enough to read and click.
        toast.error(
          <FeedErrorToast count={result.errors.length} errors={result.errors} traceId={result.traceId} startedAt={result.startedAt} />,
          { duration: 10000 }
        );
      }
    } catch (error) {
      toastRunFailure(error, 'Failed to refresh feeds', startedAt);
    } finally {
      setRefreshing(false);
    }
  };

  const onBackfillDescriptions = async () => {
    setBackfilling(true);
    const startedAt = runStartedAt();
    try {
      const result = await adminApi.backfillDescriptions();

      if (result.itemsUpdated > 0) {
        toast.success(
          `Filled in ${result.itemsUpdated} description${result.itemsUpdated === 1 ? '' : 's'} across ${result.feedsProcessed} feed${result.feedsProcessed === 1 ? '' : 's'}.`
        );
      } else {
        // The common outcome once the repair has already run — say so rather than looking
        // like nothing happened.
        toast.success(
          `Nothing to fill in — all ${result.itemsExamined} item${result.itemsExamined === 1 ? '' : 's'} already hold the full text.`
        );
      }

      if (result.errors.length > 0) {
        // Links to this run's own lines in Admin → Logs; long enough to read and click.
        toast.error(
          <FeedErrorToast count={result.errors.length} errors={result.errors} traceId={result.traceId} startedAt={result.startedAt} />,
          { duration: 10000 }
        );
      }
    } catch (error) {
      toastRunFailure(error, 'Failed to backfill descriptions', startedAt);
    } finally {
      setBackfilling(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (feed: AdminRssFeed) => {
    setEditing(feed);
    setForm({
      name: feed.name,
      url: feed.url,
      description: feed.description,
      productType: feed.productType,
      isActive: feed.isActive,
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditing(null);
  };

  const productTypeName = (value: ProductType): string =>
    productTypes.find((t) => t.value === value)?.name ?? ProductType[value];

  const onSave = async () => {
    if (!form.name.trim() || !form.url.trim()) {
      toast.error('Name and URL are required');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await adminApi.updateFeed(editing.id, {
          name: form.name,
          url: form.url,
          description: form.description,
          productType: form.productType,
          isActive: form.isActive,
        });
        toast.success('Feed updated');
      } else {
        await adminApi.createFeed({
          name: form.name,
          url: form.url,
          description: form.description,
          productType: form.productType,
        });
        toast.success('Feed created');
      }
      closeModal();
      await loadFeeds();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save feed'));
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (feed: AdminRssFeed) => {
    if (!window.confirm(`Delete feed "${feed.name}"? The feed stops updating and its notes are archived — kept in the system (visible via the "Deleted" filter), not erased.`)) return;
    setBusyId(feed.id);
    try {
      await adminApi.deleteFeed(feed.id);
      toast.success('Feed deleted');
      await loadFeeds();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete feed'));
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex h-48 items-center justify-center">
        <Spinner className="h-8 w-8 text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">{feeds.length} feed{feeds.length === 1 ? '' : 's'}</p>
        <div className="flex flex-wrap items-center gap-2">
          {/* Two buttons that sound alike and do very different things - the tooltips spell out
              which one produces versions, because confusing them cost a test round on SC-278. */}
          <button
            onClick={onRefreshFeeds}
            disabled={refreshing}
            className="btn-secondary gap-2"
            title="Fetch every active feed now, instead of waiting for the 06:00 UTC run: adds new notes, records a new version of any note Fenergo has edited, and archives notes that are gone from the feed."
          >
            {refreshing ? <Spinner /> : <DownloadCloud className="h-4 w-4" />}
            {refreshing ? 'Refreshing…' : 'Refresh feeds now'}
          </button>
          {/* SC-235 repair. Items stored before that fix kept their first-paragraph-only text,
              because ContentHash excludes the description so a normal re-fetch skips them. */}
          <button
            onClick={onBackfillDescriptions}
            disabled={backfilling}
            className="btn-secondary gap-2"
            title="Repair only: fill in release-note text that was truncated when the item was first stored. Does not add, version or archive anything. Safe to run more than once."
          >
            {backfilling ? <Spinner /> : <RefreshCw className="h-4 w-4" />}
            {backfilling ? 'Backfilling…' : 'Backfill descriptions'}
          </button>
          <button onClick={openCreate} className="btn-primary gap-2">
            <Plus className="h-4 w-4" />
            Add feed
          </button>
        </div>
      </div>

      {feeds.length === 0 ? (
        <div className="card">
          <EmptyState icon={Rss} title="No feeds" description="Add the first RSS feed to start aggregating updates." />
        </div>
      ) : (
        <div className="card overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-border text-left">
              <thead>
                <tr className="bg-muted/50">
                  <th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-muted-foreground">Feed</th>
                  <th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-muted-foreground">Product</th>
                  <th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-muted-foreground">Status</th>
                  <th className="px-4 py-2.5 text-right text-xs font-medium uppercase tracking-wider text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {feeds.map((feed) => (
                  <tr key={feed.id} className="hover:bg-muted/50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-foreground">{feed.name}</div>
                      <div className="max-w-md truncate text-sm text-muted-foreground" title={feed.url}>{feed.url}</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground">{productTypeName(feed.productType)}</td>
                    <td className="px-4 py-3">
                      <span className={cn(feed.isActive ? 'badge-success' : 'badge-neutral')}>
                        {feed.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1" aria-busy={busyId === feed.id}>
                        {busyId === feed.id && <Spinner className="mr-1 h-4 w-4 text-muted-foreground" />}
                        <button
                          onClick={() => openEdit(feed)}
                          disabled={busyId === feed.id}
                          className="btn-icon btn-ghost h-8 w-8"
                          title="Edit feed"
                          aria-label={`Edit feed ${feed.name}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => onDelete(feed)}
                          disabled={busyId === feed.id}
                          className="btn-icon btn-ghost h-8 w-8 text-danger hover:text-danger"
                          title="Delete feed"
                          aria-label={`Delete feed ${feed.name}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-secondary-950/50 p-4 backdrop-blur-xs animate-fade-in">
          <div className="surface-overlay w-full max-w-lg animate-slide-up p-6 shadow-modal">
            <h2 className="mb-4 text-xl font-bold text-foreground">{editing ? 'Edit feed' : 'Add feed'}</h2>
            <div className="space-y-4">
              <div>
                <label htmlFor="feed-name" className="label">Name *</label>
                <input
                  id="feed-name"
                  className="input mt-1.5"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Feed name"
                />
              </div>
              <div>
                <label htmlFor="feed-url" className="label">URL *</label>
                <input
                  id="feed-url"
                  className="input mt-1.5"
                  value={form.url}
                  onChange={(e) => setForm({ ...form, url: e.target.value })}
                  placeholder="https://docs.example.com/rss.xml"
                />
              </div>
              <div>
                <label htmlFor="feed-description" className="label">Description</label>
                <textarea
                  id="feed-description"
                  className="input mt-1.5 resize-none"
                  rows={2}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="What this feed covers"
                />
              </div>
              <div>
                <label htmlFor="feed-product" className="label">Product type</label>
                <select
                  id="feed-product"
                  className="input mt-1.5"
                  value={form.productType}
                  onChange={(e) => setForm({ ...form, productType: Number(e.target.value) as ProductType })}
                >
                  {productTypes.map((t) => (
                    <option key={t.value} value={t.value}>{t.name}</option>
                  ))}
                </select>
              </div>
              {editing && (
                <label className="flex items-center gap-2 text-sm text-foreground">
                  <input
                    type="checkbox"
                    checked={form.isActive}
                    onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                  />
                  Active (polled on schedule)
                </label>
              )}
            </div>
            <div className="mt-6 flex gap-2">
              <button type="button" onClick={closeModal} className="btn-outline flex-1">Cancel</button>
              <button type="button" onClick={onSave} disabled={saving} className="btn-primary flex-1">
                {saving && <Spinner className="text-primary-foreground" />}
                {saving ? 'Saving…' : editing ? 'Save changes' : 'Add feed'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
